import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, ChevronLeft, Plus, X, ChevronRight } from 'lucide-react'
import { logError } from '@/utils/logger'
import { getBorderColor, getBorderWidth, getButtonColor } from '../utils/orderPopupUtils'
import { MapPanel, AddressTypeSelector, ApartmentInput } from './MapPanel'
import { useSaveNewAddressMutation } from '../../../features/csFlow/csFlowAPI'
import notAddressIcon from '../../../assets/images/NotAddress.svg'
import locationAddIcon from '../../../assets/images/location_add.svg'

interface Address {
  id: number
  label?: string
  address?: string
  descriptiveAddress?: string
  type?: string
  addressName?: string
}

interface DeliveryComponentsProps {
  isLoadingAddresses: boolean
  addressStep: 'noAddresses' | 'savedAddresses' | 'addNewAddress'
  savedAddresses: Address[]
  selectedAddress: Address | null
  onAddressSelected: (address: Address) => void
  onAddNewAddress: () => void
  onDone: () => void
  onBack?: () => void
  onAddressSaved?: () => void // Callback after address is saved successfully
  userId: number
  isRTL: boolean
}

/**
 * Address selection section component (for main step)
 */
export const AddressSelectionSection: React.FC<{
  selectedAddress: Address | null
  selectedAddressText?: string
  onTap: () => void
  onClear?: () => void
  isRTL: boolean
}> = ({ selectedAddress, selectedAddressText, onTap, onClear, isRTL }) => {
  const { t } = useTranslation()
  const hasSavedAddress = !!selectedAddress
  const trimmedLabel = selectedAddress?.label?.trim()
  const trimmedDescriptive = selectedAddress?.descriptiveAddress?.trim()
  const trimmedAddress = selectedAddress?.address?.trim()
  const manualText = selectedAddressText?.trim()

  const getAddressTypeLabel = (): string | null => {
    if (!selectedAddress?.type) return null
    const normalizedType = selectedAddress.type.trim().toLowerCase().replace(/\s+/g, '_')
    if (!normalizedType) return null
    const translationKey = `cs.address_type_${normalizedType}`
    const translated = t(translationKey)
    return translated !== translationKey ? translated : selectedAddress.type.trim()
  }

  const addressTypeLabel = getAddressTypeLabel()

  const mainText = (hasSavedAddress && typeof selectedAddress === 'object')
    ? addressTypeLabel || trimmedLabel || trimmedDescriptive || trimmedAddress || (t('cs.selected_address') || 'تم اختيار العنوان')
    : manualText || (t('cs.delivery_address_placeholder') || 'اختر عنوان التوصيل')

  const hasAddressValue = Boolean(hasSavedAddress || manualText)

  return (
    <div className="w-full">
      <h3 className="text-base font-semibold text-gray-700 mb-3 font-['IBM_Plex_Sans_Arabic']">
        {t('cs.select_delivery_address') || 'عنوان التوصيل'}
      </h3>

      <button
        onClick={onTap}
        className={`
          w-full h-14 px-4 rounded-xl flex items-center justify-between gap-3
          border border-gray-300 bg-white hover:border-gray-400 transition-all
          flex-row-reverse
        `}
      >
    
        <div className="flex items-center gap-2">
          {hasAddressValue && onClear && (
            <div
              onClick={(e) => {
                e.stopPropagation()
                onClear()
              }}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onClear()
                }
              }}
            >
              <X className="w-4 h-4 text-gray-600" />
            </div>
          )}
          {!hasAddressValue && (
            isRTL ? <ChevronLeft className="w-5 h-5 text-gray-600" /> : <ChevronRight className="w-5 h-5 text-gray-600" />
          )}
        </div>
        <div className={`flex items-start gap-3  flex-row-reverse`}>
          <div className={`flex-1 `}>
            <p
              className={`text-sm font-semibold font-['IBM_Plex_Sans_Arabic'] ${
                hasAddressValue ? 'text-gray-700' : 'text-gray-500'
              }`}
            >
              {mainText}
            </p>
          </div>
        </div>
      </button>
    </div>
  )
}

/**
 * Build address selection section (for main step) - wrapper for backward compatibility
 */
export const buildAddressSelectionSection = (props: {
  selectedAddress: Address | null
  selectedAddressText?: string
  onTap: () => void
  onClear?: () => void
  isRTL: boolean
}): React.ReactElement => {
  return <AddressSelectionSection {...props} />
}

/**
 * Complete address flow component
 */
export const CompleteAddressFlow: React.FC<DeliveryComponentsProps> = ({
  isLoadingAddresses,
  addressStep,
  savedAddresses,
  selectedAddress,
  onAddressSelected,
  onAddNewAddress,
  onDone,
  onBack,
  onAddressSaved,
  userId,
  isRTL,
}) => {
  const { t } = useTranslation()
  const [selectedLatLng, setSelectedLatLng] = useState<{ lat: number; lng: number } | null>(null)
  const [selectedAddressText, setSelectedAddressText] = useState('')
  const [selectedAddressType, setSelectedAddressType] = useState('home')
  const [apartmentNumber, setApartmentNumber] = useState('')
  const [saveNewAddress, { isLoading: isSaving }] = useSaveNewAddressMutation()

  const handleLocationSelected = (lat: number, lng: number, address: string) => {
    setSelectedLatLng({ lat, lng })
    setSelectedAddressText(address)
  }

  const handleSaveNewAddress = async () => {
    if (!selectedLatLng) {
      // Show error - location not selected
      return
    }

    if (!apartmentNumber.trim()) {
      // Show error - apartment number required
      return
    }

    try {
      const result = await saveNewAddress({
        userId,
        name: apartmentNumber.trim(),
        latitude: selectedLatLng.lat,
        longitude: selectedLatLng.lng,
        descriptiveAddress: selectedAddressText || apartmentNumber.trim(),
        type: selectedAddressType,
      }).unwrap()

      // If we have data in response, select the address
      if (result && result.data) {
        onAddressSelected({
          id: result.data.id,
          label: result.data.label,
          address: result.data.address,
          descriptiveAddress: result.data.descriptive_address,
          type: result.data.type,
        })
      }
      
      // Always navigate to saved addresses page after successful save
      // (unwrap() succeeded means the request was successful)
      // Works for both 200 OK with body and 204 No Content
      if (onAddressSaved) {
        onAddressSaved()
      } else {
        onDone()
      }
    } catch (error) {
      // Handle error - don't navigate if save failed
      logError('Error saving address:', error)
    }
  }

  if (isLoadingAddresses) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]"></div>
        <p className="ml-3 text-sm text-gray-600 font-['IBM_Plex_Sans_Arabic']">
          {t('cs.loading_addresses') || 'جاري تحميل العناوين...'}
        </p>
      </div>
    )
  }
  
  switch (addressStep) {
    case 'noAddresses':
      return buildNoAddressesPopup({ onAddNewAddress, isRTL, t })

    case 'savedAddresses':
      return buildSavedAddressesPopup({
        savedAddresses,
        selectedAddress,
        onAddressSelected,
        onAddNewAddress,
        onDone,
        isRTL,
        t,
      })

    case 'addNewAddress':
      return buildAddNewAddressPopup({
        selectedLatLng,
        selectedAddressText,
        selectedAddressType,
        apartmentNumber,
        onLocationSelected: handleLocationSelected,
        onAddressTypeChange: setSelectedAddressType,
        onApartmentChange: setApartmentNumber,
        onSave: handleSaveNewAddress,
        onBack,
        isSaving,
        isRTL,
        t,
      })

    default:
      return <div />
  }
}

/**
 * Build complete address flow - wrapper for backward compatibility
 */
export const buildCompleteAddressFlow = (props: DeliveryComponentsProps): React.ReactElement => {
  return <CompleteAddressFlow {...props} />
}

/**
 * Build no addresses popup
 */
const buildNoAddressesPopup = ({
  onAddNewAddress,
  isRTL,
  t,
}: {
  onAddNewAddress: () => void
  isRTL: boolean
  t: (key: string) => string
}): React.ReactElement => {

  return (
    <div className="flex flex-col items-center justify-between py-5 px-4 h-full">
   <div className='w-full  h-15'/>
      <div className='flex flex-col items-center justify-center mt-0 '>

        {/* Icon */}
        <div className="w-20 h-20 flex items-center justify-center ">
          <img
            src={notAddressIcon}
            alt="No addresses"
            className="w-full h-full object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
        </div>

            {/* Title */}
            <h3 className="text-xl font-medium text-gray-900  font-['IBM_Plex_Sans_Arabic'] text-center">
              {t('cs.no_saved_addresses') || 'لا توجد عناوين محفوظة'}
            </h3>
              {/* Subtitle */}
              <p className="text-base text-gray-600  font-['IBM_Plex_Sans_Arabic'] text-center">
              {t('cs.add_new_address_to_start') || 'أضف عنوان جديد للبدء'}
            </p>
      </div>
  
      {/* Add New Address Button */}
      <button
        onClick={onAddNewAddress}
        className={`
          w-full h-[48px] rounded-xl flex items-center justify-center gap-2
          font-medium text-lg font-['IBM_Plex_Sans_Arabic']
          transition-all hover:opacity-90
          bg-white border border-gray-300
        `}
        style={{
          color: '#000000',
        }}
      >
        <Plus className="w-5 h-5 text-flutter-button" />
        <span>{t('cs.add_new_address') || 'إضافة عنوان جديد'}</span>
      </button>
    </div>
  )
}

/**
 * Build saved addresses popup
 */
const buildSavedAddressesPopup = ({
  savedAddresses,
  selectedAddress,
  onAddressSelected,
  onAddNewAddress,
  onDone,
  isRTL,
  t,
}: {
  savedAddresses: Address[]
  selectedAddress: Address | null
  onAddressSelected: (address: Address) => void
  onAddNewAddress: () => void
  onDone: () => void
  isRTL: boolean
  t: (key: string) => string
}): React.ReactElement => {

  return (
    <div className="flex flex-col h-full px-[15px]">
      {/* Title */}
      <h3 className="text-base font-normal text-gray-900 mb-3 font-['IBM_Plex_Sans_Arabic']">
        {t('cs.saved_addresses') || 'العناوين المحفوظة'}
      </h3>

      {/* Addresses List */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pb-[90px]">
        {savedAddresses.map((address) => {
          const isSelected = selectedAddress?.id === address.id

          return (
            <button
              key={address.id}
              onClick={() => onAddressSelected(address)}
              className={`
                w-full p-5 rounded-2xl flex items-center justify-between
                transition-all
                flex-row-reverse 
              `}
              style={{
                border: `${getBorderWidth(isSelected)}px solid ${getBorderColor(isSelected)}`,
                backgroundColor: '#FFFFFF',
                boxShadow: isSelected ? '0px 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
              }}
            >
              {/* Radio Button */}
              <div
                className={`
                  w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                  ${isRTL ? 'mr-3' : 'ml-3'}
                `}
                style={{
                  border: `2px solid ${isSelected ? 'var(--brand-primary)' : '#D1D5DB'}`,
                  backgroundColor: isSelected ? 'var(--brand-primary)' : 'transparent',
                }}
              >
                {isSelected && (
                  <div className="w-3.5 h-3.5 rounded-full bg-white" />
                )}
              </div>

              <div className={`flex items-center gap-2`}> 
              <img
                  src={locationAddIcon}
                  alt="location"
                  className="w-9 h-9 flex-shrink-0"
                  style={{ filter: isSelected ? 'none' : 'opacity(0.5)' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.style.display = 'none'
                  }}
                />
                <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <p
                    className="text-base font-bold mb-1.5 font-['IBM_Plex_Sans_Arabic'] line-clamp-1"
                    style={{ color: isSelected ? 'var(--brand-primary)' : '#111827' }}
                  >
                    {typeof address.type === 'string' ? address.type : 'No Type'}
                  </p>
                  <p className="text-sm font-normal text-gray-600 font-['IBM_Plex_Sans_Arabic'] line-clamp-2">
                    {typeof address.descriptiveAddress === 'string' ? address.descriptiveAddress : (typeof address.address === 'string' ? address.address : 'No Address')}
                  </p>
                </div>
               
              </div>
            </button>
          )
        })}
      </div>

      {/* Bottom Buttons */}
      <div className="pt-6 pb-6 flex gap-4 sticky bottom-0 z-10 bg-white/95 backdrop-blur-sm">
        {/* Done Button */}
        <button
          onClick={onDone}
          disabled={!selectedAddress}
          className={`
            flex-1 h-[52px] rounded-xl flex items-center justify-center
            font-bold text-lg font-['IBM_Plex_Sans_Arabic']
            transition-all
            ${selectedAddress ? 'hover:opacity-90' : 'cursor-not-allowed opacity-50'}
          `}
          style={{
            backgroundColor: selectedAddress ? getButtonColor(true) : '#E5E7EB',
            color: selectedAddress ? '#FFFFFF' : '#9CA3AF',
          }}
        >
          {t('common.done') || 'تم'}
        </button>

        {/* Add New Address Button */}
        <button
          onClick={onAddNewAddress}
          className={`
            flex-1 h-[52px] rounded-xl flex items-center justify-center gap-2
            border border-gray-300 bg-white hover:border-gray-400 transition-all
            font-semibold text-base font-['IBM_Plex_Sans_Arabic']
          `}
        >
          <Plus className="w-5 h-5 text-gray-900" />
          <span className="text-gray-900">{t('cs.add_new_address') || 'إضافة عنوان جديد'}</span>
        </button>
      </div>
    </div>
  )
}

/**
 * Build add new address popup
 */
const buildAddNewAddressPopup = ({
  selectedLatLng,
  selectedAddressText,
  selectedAddressType,
  apartmentNumber,
  onLocationSelected,
  onAddressTypeChange,
  onApartmentChange,
  onSave,
  onBack,
  isSaving,
  isRTL,
  t,
}: {
  selectedLatLng: { lat: number; lng: number } | null
  selectedAddressText: string
  selectedAddressType: string
  apartmentNumber: string
  onLocationSelected: (lat: number, lng: number, address: string) => void
  onAddressTypeChange: (type: string) => void
  onApartmentChange: (value: string) => void
  onSave: () => void
  onBack?: () => void
  isSaving: boolean
  isRTL: boolean
  t: (key: string) => string
}): React.ReactElement => {
  // Extract location code and address parts from selectedAddressText
  const extractAddressParts = (address: string) => {
    // Try to extract location code (e.g., PM7G+C4F) and split address
    const locationCodeMatch = address.match(/([A-Z0-9]+\+[A-Z0-9]+)/)
    const locationCode = locationCodeMatch ? locationCodeMatch[1] : ''
    
    // Extract Arabic city name (e.g., "ريناد")
    const arabicCityMatch = address.match(/[\u0600-\u06FF]+/)
    const arabicCity = arabicCityMatch ? arabicCityMatch[0] : ''
    
    // Extract English address part
    const englishPart = address.replace(locationCode, '').replace(arabicCity, '').trim()
    
    return { locationCode, arabicCity, englishPart, fullAddress: address }
  }

  const addressParts = selectedAddressText ? extractAddressParts(selectedAddressText) : {
    locationCode: '',
    arabicCity: '',
    englishPart: '',
    fullAddress: ''
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-[18px] pt-[18px] pb-4 flex items-center justify-start gap-2 flex-shrink-0">
        <button
          onClick={onBack}
          className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-gray-300 transition-colors hover:bg-gray-50"
        >
          {isRTL ? <ChevronRight className="w-5 h-5 text-gray-600" /> : <ChevronLeft className="w-5 h-5 text-gray-600" />}
        </button>
        <h2 className="text-lg font-semibold font-['IBM_Plex_Sans_Arabic'] text-gray-900">
          {t('cs.add_new_address') || 'اضافة عنوان جديد'}
        </h2>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Map Section with Search Bar */}
      <div className="flex-1 flex flex-col min-h-0" style={{ height: '60%' }}>
        <MapPanel
          key="add-new-address-map"
          selectedLatLng={selectedLatLng}
          selectedAddressText={selectedAddressText}
          onLocationSelected={onLocationSelected}
          isRTL={isRTL}
        />
      </div>

      {/* Address Details Section */}
      <div className="flex flex-col bg-white  relative" style={{ height: '40%' }}>
        <div className="flex-1 overflow-y-auto px-4 pt-4 pb-5">
          <div className="space-y-4">
            {/* Address Details Title */}
            <h3 className="text-base font-semibold text-gray-800 font-['IBM_Plex_Sans_Arabic']">
              {t('cs.address_details') || 'تفاصيل العنوان'}
            </h3>

            {/* Displayed Address - Shows location code + Arabic city + English address */}
            <div className=" rounded-xl ">
              {selectedAddressText ? (
                <div className="space-y-1">
                  
                  {addressParts.englishPart && (
                    <p className="text-sm font-normal text-gray-700 font-['IBM_Plex_Sans_Arabic']">
                      {addressParts.englishPart}
                    </p>
                  )}
                  {!addressParts.locationCode && !addressParts.arabicCity && !addressParts.englishPart && (
                    <p className="text-sm font-normal text-gray-700 font-['IBM_Plex_Sans_Arabic']">
                      {selectedAddressText}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm font-normal text-gray-500 font-['IBM_Plex_Sans_Arabic']">
                  {t('cs.select_location_on_map') || 'اختر الموقع على الخريطة'}
                </p>
              )}
            </div>

            {/* Address Type Selector */}
            <AddressTypeSelector
              selectedType={selectedAddressType}
              onTypeChange={onAddressTypeChange}
              isRTL={isRTL}
            />

            {/* Apartment/Villa Input */}
            <div className="w-full">
              <label className="text-sm font-semibold text-gray-700 mb-2 block font-['IBM_Plex_Sans_Arabic']">
                {t('cs.apartment_villa') || 'شقة / فيلا'}
              </label>
              <input
                type="text"
                value={apartmentNumber}
                onChange={(e) => onApartmentChange(e.target.value)}
                placeholder={t('cs.fifth_floor_apartment_4') || 'الدور الخامس شقة ٤'}
                className={`
                  w-full h-12 px-4 rounded-xl border border-gray-300 bg-white
                  text-sm font-medium text-gray-800 font-['IBM_Plex_Sans_Arabic']
                  focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent
                  ${isRTL ? 'text-right' : 'text-left'}
                  placeholder:text-gray-400
                `}
              />
            </div>
          </div>
        </div>

        {/* Save Button - Fixed at bottom */}
        <div className="sticky bottom-0 px-4 pb-4 pt-4 border-t border-gray-200 bg-white z-10">
          <button
            onClick={onSave}
            disabled={!selectedLatLng || !apartmentNumber.trim() || isSaving}
            className={`
              w-full h-[52px] rounded-xl flex items-center justify-center
              font-semibold text-base font-['IBM_Plex_Sans_Arabic']
              transition-all shadow-sm
              ${selectedLatLng && apartmentNumber.trim() && !isSaving
                ? 'hover:opacity-90 active:scale-[0.98]'
                : 'cursor-not-allowed opacity-50'
              }
            `}
            style={{
              backgroundColor: selectedLatLng && apartmentNumber.trim() && !isSaving
                ? 'var(--brand-primary)' // Teal-green color as shown in image
                : '#E5E7EB',
              color: selectedLatLng && apartmentNumber.trim() && !isSaving ? '#FFFFFF' : '#9CA3AF',
            }}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                <span>{t('cs.saving') || 'جاري الحفظ...'}</span>
              </>
            ) : (
              <span>{t('common.save') || 'حفظ'}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}






